import { Client } from "@/Common/Api/Client";
import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { ITextboxProps, TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseWorkSafeBcSubSection, IBaseWorkSafeBcSubSectionProps } from "../BaseWorkSafeBcSubSection";
import { WorkSafeBcAnatomicalPosition } from "../Constants/WorkSafeBcAnatomicalPosition";
import { WorkSafeBcCodeDictionaryType } from "../Constants/WorkSafeBcCodeDictionaryType";
import { WorkSafeBcFormValidator } from "../Models/WorkSafeBcFormValidator";

enum WorkSafeBcInjuryCodeField {
    AreaOfInjury,
    NatureOfInjury,
    Diagnostic
}

interface IWorkSafeBcInjuryCodesSectionState {
    lockedFields: WorkSafeBcInjuryCodeField[];
}

class WorkSafeBcInjuryCodesSection extends BaseWorkSafeBcSubSection<IBaseWorkSafeBcSubSectionProps, IWorkSafeBcInjuryCodesSectionState> {
    public constructor(props: IBaseWorkSafeBcSubSectionProps) {
        super(props);
        const lockedFields: WorkSafeBcInjuryCodeField[] = [];

        if (props.model.AreaOfInjuryCode?.trim()?.length > 0)
            lockedFields.push(WorkSafeBcInjuryCodeField.AreaOfInjury);

        if (props.model.NatureOfInjuryCode?.trim()?.length > 0)
            lockedFields.push(WorkSafeBcInjuryCodeField.NatureOfInjury);

        if (props.model.DiagnosticCode?.trim()?.length > 0)
            lockedFields.push(WorkSafeBcInjuryCodeField.Diagnostic);

        this.state = {
            lockedFields: lockedFields
        };
    }

    public render(): JSX.Element {
        const data = this.props.model;
        const validator = new WorkSafeBcFormValidator(data);

        return (
            <div className="flex-grow-container subsection-border-top">
                <div className="row">
                    <div className="col-md-12">
                        <label className="form-label bold f16px">{this.getString("WorkSafeBc_InjuryCodesAndDescriptionsLabel")}</label>
                    </div>
                </div>
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBC_DiagnosisTextLabel",
                        value: JSXHelper.BreakStringIntoParagraphs(data.Diagnosis),
                        required: true
                    },
                    <TextboxControl
                        onChange={value => this.props.mutateModelState(s => s.Diagnosis = value)}
                        value={data.Diagnosis}
                        maxLength={50}
                        valid={validator.isDiagnosisTextValid()}
                        style={{ maxWidth: "550px" }}
                    />
                )}
                <div className="row">
                    {this.renderFields(
                        {
                            labelValueKey: "WorkSafeBc_AreaOfInjuryLabel",
                            value: data.getAreaOfInjuryAndPositionString(),
                            hasParentClassName: true,
                            className: "col-md-4",
                            required: true
                        },
                        <div style={{ display: "flex", justifyContent: "flex-start" }}>
                            {this.renderLockingField(
                                WorkSafeBcInjuryCodeField.AreaOfInjury,
                                () => this.props.mutateModelState(s => {
                                    s.AreaOfInjuryCode = "";
                                    s.AreaOfInjuryCodeDescription = "";
                                }),
                                {
                                    ...this.getLockingFieldCommonProps(),
                                    value: data.getAreaOfInjuryCodeString(),
                                    valid: validator.isAreaOfInjuryCodeValid(),
                                    onAutoCompleteSelectCallBack: (code, model, value) => this.mutateState(
                                        s => s.lockedFields.push(WorkSafeBcInjuryCodeField.AreaOfInjury),
                                        () => this.props.mutateModelState(s => {
                                            s.AreaOfInjuryCode = code;
                                            s.AreaOfInjuryCodeDescription = value;
                                        })
                                    ),
                                    onSearchCallback: (searchText: string) => this.searchDictionary(searchText, WorkSafeBcCodeDictionaryType.BodyParts)
                                }
                            )}
                            <SelectKVPControl
                                Value={data.AnatomicalPosition?.toString() ?? ""}
                                Callback={position => {
                                    data.setAnatomicalPositionFromString(position);
                                    this.props.mutateModelState(s => s.AnatomicalPosition = data.AnatomicalPosition);
                                }}
                                IncludeEmptyChoice
                                AvailableValues={this.getAnatomicalPositionOptions()}
                                style={{ marginLeft: "5px", width: "150px", height: "34px" }}
                                isInvalid={!validator.isAnatomicalPositionValid()}
                            />
                        </div>
                    )}
                    {this.renderFields(
                        {
                            labelValueKey: "WorkSafeBc_NatureOfInjuryCodeLabel",
                            value: data.getNatureOfInjuryCodeString(),
                            hasParentClassName: true,
                            className: "col-md-4",
                            required: true
                        },
                        this.renderLockingField(
                            WorkSafeBcInjuryCodeField.NatureOfInjury,
                            () => this.props.mutateModelState(s => {
                                s.NatureOfInjuryCode = "";
                                s.NatureOfInjuryCodeDescription = "";
                            }),
                            {
                                ...this.getLockingFieldCommonProps(),
                                value: data.getNatureOfInjuryCodeString(),
                                valid: validator.isNatureOfInjuryCodeValid(),
                                onAutoCompleteSelectCallBack: (code, model, value) => this.mutateState(
                                    s => s.lockedFields.push(WorkSafeBcInjuryCodeField.NatureOfInjury),
                                    () => this.props.mutateModelState(s => {
                                        s.NatureOfInjuryCode = code;
                                        s.NatureOfInjuryCodeDescription = value;
                                    })
                                ),
                                onSearchCallback: (searchText: string) => this.searchDictionary(searchText, WorkSafeBcCodeDictionaryType.NatureOfInjury)
                            }
                        )
                    )}
                    {this.renderFields(
                        {
                            labelValueKey: "WorkSafeBc_DiagnosticCodeLabel",
                            value: data.getDiagnosticCodeString(),
                            hasParentClassName: true,
                            className: "col-md-4",
                            required: true
                        },
                        this.renderLockingField(
                            WorkSafeBcInjuryCodeField.Diagnostic,
                            () => this.props.mutateModelState(s => {
                                s.DiagnosticCode = "";
                                s.DiagnosticCodeDescription = "";
                            }),
                            {
                                ...this.getLockingFieldCommonProps(),
                                valid: validator.isDiagnosticCodeValid(),
                                value: data.getDiagnosticCodeString(),
                                onAutoCompleteSelectCallBack: (code, model, value) => this.mutateState(
                                    s => s.lockedFields.push(WorkSafeBcInjuryCodeField.Diagnostic),
                                    () => this.props.mutateModelState(s => {
                                        s.DiagnosticCode = code;
                                        s.DiagnosticCodeDescription = value;
                                    })
                                ),
                                onSearchCallback: (searchText: string) => this.searchIcd9Dictionary(searchText)
                            }
                        )
                    )}
                </div>
            </div>
        );
    }

    private renderLockingField(field: WorkSafeBcInjuryCodeField, onRemove: () => void, props: ITextboxProps): JSX.Element {
        return props.value?.trim()?.length > 0 && this.state.lockedFields.contains(field)
            ? (
                <div className="flex-display flex-align-items-center flex-grow-1" style={{ minHeight: "34px" }}>
                    <span className="margin-xl-right">{props.value}</span>
                    <RemoveButton
                        onRemove={() => this.mutateState(
                            s => s.lockedFields = s.lockedFields.filter(lockedField => lockedField !== field),
                            () => onRemove()
                        )}
                    />
                </div>
            )
            : <TextboxControl {...props} />;
    }

    private async searchDictionary(searchString: string, codeType: WorkSafeBcCodeDictionaryType): Promise<SearchableItemModelT<any>[]> {
        return this.handleSearch(
            ServerOperations.Server.MedfarDictionnary_SearchWorkSafeBcCodeDictionary,
            searchString,
            codeType
        );
    }

    private async searchIcd9Dictionary(searchString: string): Promise<SearchableItemModelT<any>[]> {
        return this.handleSearch(
            ServerOperations.Server.MedfarDictionnary_SearchWorkSafeBcIcd9CodeDictionary,
            searchString
        );
    }

    private async handleSearch(operationNumber: ServerOperations.Server, searchString: string, codeType: WorkSafeBcCodeDictionaryType = null): Promise<SearchableItemModelT<any>[]> {
        const response = await Client.postAsync<{ WorkSafeBcCodes: { Code: string; Description: string; }[]; }>(
            operationNumber,
            {
                SearchString: searchString,
                CodeType: codeType
            }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return [];
        }

        return response.data.WorkSafeBcCodes.map(p => {
            const displayValue = p.Code + " " + p.Description;
            return new SearchableItemModelT(displayValue, p.Code, null, displayValue);
        });
    }

    private getLockingFieldCommonProps(): ITextboxProps {
        return {
            debounceMs: 300,
            flexGrow: 1,
            noAutoCompleteStyle: { flexGrow: 1 },
            listWidth: "400px"
        };
    }

    private getAnatomicalPositionOptions(): KeyValuePair<any, string>[] { // TODO: we shouldn't use `any` here
        return [
            new KeyValuePair(WorkSafeBcAnatomicalPosition.Left, this.getString("WorkSafeBc_AnatomicalPositionOption_Left")),
            new KeyValuePair(WorkSafeBcAnatomicalPosition.Right, this.getString("WorkSafeBc_AnatomicalPositionOption_Right")),
            new KeyValuePair(WorkSafeBcAnatomicalPosition.LeftAndRight, this.getString("WorkSafeBc_AnatomicalPositionOption_LeftAndRight")),
            new KeyValuePair(WorkSafeBcAnatomicalPosition.NotApplicable, this.getString("WorkSafeBc_AnatomicalPositionOption_NotApplicable"))
        ];
    }
}

export { WorkSafeBcInjuryCodesSection };
